<template>
  <div>
    <!-- Manual Install Button (Always visible for testing/fallback) -->
    <v-card
      v-if="!isStandalone && showManualInstall"
      :class="['install-prompt-card', 'manual-install', { 'is-mobile': isMobile, 'is-closed': isClosed }]"
      elevation="2"
    >
      <v-card-text :class="['pa-0', { 'banner-layout': !isMobile, 'mobile-layout': isMobile }]">
        <div v-if="!isMobile" class="banner-content-desktop d-flex align-center flex-nowrap">
          <v-icon :size="28" color="primary" class="mr-3 flex-shrink-0 banner-icon">mdi-cellphone-link</v-icon>
          <div class="mr-4 flex-shrink-1 min-width-0 banner-text-container">
            <div class="install-title">Install App</div>
            <div class="install-subtitle">Add to your home screen for quick access</div>
          </div>
          <div class="banner-actions d-flex align-center flex-shrink-0">
            <div class="dismiss-section d-flex align-center mr-3 flex-shrink-0" @click="dismissManualInstall">
              <v-icon color="#718096" size="18" class="mr-1">mdi-close</v-icon>
              <span class="dismiss-text">Dismiss</span>
            </div>
            <div class="action-buttons flex-shrink-0">
              <v-btn
                v-if="!isIOS && deferredPrompt"
                outlined
                color="black"
                @click="handleInstall"
                :loading="installing"
                rounded
                class="install-btn-black"
              >
                <v-icon left size="18">mdi-plus</v-icon>
                Install
              </v-btn>
              <v-btn
                v-else-if="isIOS"
                outlined
                color="black"
                @click="showIOSInstructions = true"
                rounded
                class="install-btn-black"
              >
                <v-icon left size="18">mdi-plus</v-icon>
                Show Instructions
              </v-btn>
              <v-btn
                v-else
                outlined
                color="black"
                @click="showBrowserInstructions = true"
                rounded
                class="install-btn-black"
              >
                <v-icon left size="18">mdi-plus</v-icon>
                How to Install
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else class="mobile-content">
          <div class="d-flex align-center mb-3">
            <v-icon :size="24" color="primary" class="mr-3">mdi-cellphone-link</v-icon>
            <div class="flex-grow-1">
              <div class="install-title">Install App</div>
              <div class="install-subtitle">Add to your home screen for quick access</div>
            </div>
            <v-btn
              icon
              small
              @click="dismissManualInstall"
              class="ml-2"
            >
              <v-icon color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="mobile-actions">
            <v-checkbox
              v-model="dontShowAgain"
              label="Don't show me this message again"
              color="primary"
              hide-details
              class="dont-show-checkbox mb-2"
            ></v-checkbox>
            <div class="action-buttons">
              <v-btn
                v-if="!isIOS && deferredPrompt"
                color="primary"
                @click="handleInstall"
                :loading="installing"
                block
                rounded
              >
                Install
              </v-btn>
              <v-btn
                v-else-if="isIOS"
                color="primary"
                @click="showIOSInstructions = true"
                block
                rounded
              >
                Show Instructions
              </v-btn>
              <v-btn
                v-else
                dark
                color="black"
                @click="showBrowserInstructions = true"
                block
                rounded
              >
                How to Install
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Android/Chrome Install Prompt (Automatic) -->
    <v-card
      v-if="showInstallPrompt && !isIOS && !showManualInstall"
      :class="['install-prompt-card', { 'is-mobile': isMobile, 'is-closed': isClosed }]"
      elevation="2"
    >
      <v-card-text :class="['pa-0', { 'banner-layout': !isMobile, 'mobile-layout': isMobile }]">
        <div v-if="!isMobile" class="banner-content-desktop d-flex align-center flex-nowrap">
          <v-icon :size="28" color="primary" class="mr-3 flex-shrink-0 banner-icon">mdi-download</v-icon>
          <div class="mr-4 flex-shrink-1 min-width-0 banner-text-container">
            <div class="install-title">Install Cuetrics</div>
            <div class="install-subtitle">Add to your home screen for quick access</div>
          </div>
          <div class="banner-actions d-flex align-center flex-shrink-0">
            <div class="dismiss-section d-flex align-center mr-3 flex-shrink-0" @click="dismissPrompt">
              <v-icon color="#718096" size="18" class="mr-1">mdi-close</v-icon>
              <span class="dismiss-text">Dismiss</span>
            </div>
            <v-btn
              outlined
              color="black"
              @click="handleInstall"
              :loading="installing"
              rounded
              class="install-btn-black flex-shrink-0"
            >
              <v-icon left size="18">mdi-plus</v-icon>
              Install
            </v-btn>
          </div>
        </div>
        <div v-else class="mobile-content">
          <div class="d-flex align-center mb-3">
            <v-icon :size="24" color="primary" class="mr-3">mdi-download</v-icon>
            <div class="flex-grow-1">
              <div class="install-title">Install Cuetrics</div>
              <div class="install-subtitle">Add to your home screen for quick access</div>
            </div>
            <v-btn
              icon
              small
              @click="dismissPrompt"
              class="ml-2"
            >
              <v-icon color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="mobile-actions">
            <v-checkbox
              v-model="dontShowAgain"
              label="Don't show me this message again"
              color="primary"
              hide-details
              class="dont-show-checkbox mb-2"
            ></v-checkbox>
            <v-btn
              color="primary"
              @click="handleInstall"
              :loading="installing"
              block
              rounded
            >
              Install
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Browser Install Instructions (Chrome/Edge) -->
    <v-dialog
      v-model="showBrowserInstructions"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="headline" style="color: #1a202c;">
          <v-icon class="mr-2" color="primary">mdi-download</v-icon>
          Install on Chrome/Edge
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="ios-steps">
            <div class="step-item">
              <v-icon size="20" class="step-icon" color="primary">mdi-menu</v-icon>
              <span>Click the <strong>menu</strong> icon <v-icon size="16" color="primary">mdi-dots-vertical</v-icon> in the top right corner</span>
            </div>
            <div class="step-item">
              <v-icon size="20" class="step-icon" color="primary">mdi-plus-circle</v-icon>
              <span>Look for <strong>"Install"</strong> or <strong>"Add to Home Screen"</strong> option</span>
            </div>
            <div class="step-item">
              <v-icon size="20" class="step-icon" color="primary">mdi-check</v-icon>
              <span>Click <strong>"Install"</strong> to add the app</span>
            </div>
          </div>
          <v-alert type="info" outlined dense class="mt-4">
            <small>If you don't see the install option, make sure you're using HTTPS and the app meets PWA requirements.</small>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showBrowserInstructions = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- iOS Install Instructions -->
    <v-card
      v-if="showIOSInstructions"
      :class="['install-prompt-card', 'ios-instructions', { 'is-mobile': isMobile, 'is-closed': isClosed }]"
      elevation="2"
    >
      <v-card-text :class="['pa-0', { 'banner-layout': !isMobile, 'mobile-layout': isMobile }]">
        <div v-if="!isMobile" class="banner-content-desktop d-flex align-center flex-nowrap">
          <v-icon :size="28" color="primary" class="mr-3 flex-shrink-0 banner-icon">mdi-apple</v-icon>
          <div class="mr-4 flex-shrink-1 min-width-0 banner-text-container">
            <div class="install-title">Install on iPhone/iPad</div>
            <div class="install-subtitle">Add to your home screen</div>
          </div>
          <div class="banner-actions d-flex align-center flex-shrink-0">
            <div class="dismiss-section d-flex align-center mr-3 flex-shrink-0" @click="dismissIOSInstructions">
              <v-icon color="#718096" size="18" class="mr-1">mdi-close</v-icon>
              <span class="dismiss-text">Dismiss</span>
            </div>
          </div>
        </div>
        <div v-else class="mobile-content">
          <div class="d-flex align-center mb-3">
            <v-icon :size="24" color="primary" class="mr-3">mdi-apple</v-icon>
            <div class="flex-grow-1">
              <div class="install-title">Install on iPhone/iPad</div>
              <div class="install-subtitle">Add to your home screen</div>
            </div>
            <v-btn
              icon
              small
              @click="dismissIOSInstructions"
            >
              <v-icon color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="ios-steps mb-3">
            <div class="step-item">
              <v-icon size="20" class="step-icon" color="primary">mdi-menu</v-icon>
              <span>Tap the <strong>Share</strong> button <v-icon size="16" color="primary">mdi-share-variant</v-icon> at the bottom</span>
            </div>
            <div class="step-item">
              <v-icon size="20" class="step-icon" color="primary">mdi-plus-circle</v-icon>
              <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
            </div>
            <div class="step-item">
              <v-icon size="20" class="step-icon" color="primary">mdi-check</v-icon>
              <span>Tap <strong>"Add"</strong> to confirm</span>
            </div>
          </div>
          <v-checkbox
            v-model="dontShowAgain"
            label="Don't show me this message again"
            color="primary"
            hide-details
            class="dont-show-checkbox"
          ></v-checkbox>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'PWAInstallPrompt',
  data() {
    return {
      deferredPrompt: null,
      showInstallPrompt: false,
      showIOSInstructions: false,
      showBrowserInstructions: false,
      showManualInstall: true, // Always show manual install option
      installing: false,
      dismissedInstall: false,
      dismissedIOS: false,
      dismissedManualInstall: false,
      isIOS: false,
      isStandalone: false,
      dontShowAgain: false,
      isMobile: false,
      isClosed: false
    };
  },
  mounted() {
    this.detectPlatform();
    this.checkIfInstalled();
    this.setupInstallPrompt();
    this.detectScreenSize();
    window.addEventListener('resize', this.detectScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.detectScreenSize);
  },
  watch: {
    showManualInstall(newVal) {
      if (newVal) {
        this.isClosed = false;
      }
    },
    showInstallPrompt(newVal) {
      if (newVal) {
        this.isClosed = false;
      }
    },
    showIOSInstructions(newVal) {
      if (newVal) {
        this.isClosed = false;
      }
    }
  },
  methods: {
    detectScreenSize() {
      // Consider mobile if width is less than 960px (tablet breakpoint)
      this.isMobile = window.innerWidth < 960;
    },
    
    detectPlatform() {
      // Detect iOS
      this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      // Detect if already installed (standalone mode)
      this.isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone ||
                         document.referrer.includes('android-app://');
    },
    
    checkIfInstalled() {
      // Don't show prompt if already installed
      if (this.isStandalone) {
        this.showManualInstall = false;
        return;
      }
      
      // Check if user selected "don't show again"
      const dontShowAgain = localStorage.getItem('pwa-install-dont-show-again');
      if (dontShowAgain === 'true') {
        this.showManualInstall = false;
        this.showInstallPrompt = false;
        this.showIOSInstructions = false;
        return;
      }
      
      // Check if user dismissed the prompts before
      const dismissedInstall = localStorage.getItem('pwa-install-dismissed');
      const dismissedIOS = localStorage.getItem('pwa-ios-instructions-dismissed');
      const dismissedManual = localStorage.getItem('pwa-manual-install-dismissed');
      
      this.dismissedInstall = dismissedInstall === 'true';
      this.dismissedIOS = dismissedIOS === 'true';
      this.dismissedManualInstall = dismissedManual === 'true';
      
      // Show manual install if not dismissed
      this.showManualInstall = !this.dismissedManualInstall;
      if (this.showManualInstall) {
        this.isClosed = false;
      }
      
      // Show iOS instructions if on iOS and not dismissed
      if (this.isIOS && !this.dismissedIOS && !this.isStandalone) {
        // Delay showing iOS instructions slightly
        setTimeout(() => {
          this.showIOSInstructions = true;
          this.isClosed = false;
        }, 2000);
      }
    },
    
    setupInstallPrompt() {
      // Listen for the beforeinstallprompt event (Android/Chrome)
      window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent the default browser install prompt
        e.preventDefault();
        
        // Save the event for later use
        this.deferredPrompt = e;
        
        // Show our custom install button if not dismissed and not opted out
        const dontShowAgain = localStorage.getItem('pwa-install-dont-show-again');
        if (!this.dismissedInstall && !this.isStandalone && dontShowAgain !== 'true') {
          // Delay showing the prompt slightly
          setTimeout(() => {
            this.showInstallPrompt = true;
            this.isClosed = false;
          }, 2000);
        }
      });
      
      // Listen for app installed event
      window.addEventListener('appinstalled', () => {
        this.showInstallPrompt = false;
        this.showIOSInstructions = false;
        this.showManualInstall = false;
        this.deferredPrompt = null;
        this.$store.dispatch('showSuccess', 'App installed successfully!');
      });
    },
    
    async handleInstall() {
      if (!this.deferredPrompt) {
        return;
      }
      
      this.installing = true;
      
      try {
        // Show the install prompt
        this.deferredPrompt.prompt();
        
        // Wait for the user to respond
        const { outcome } = await this.deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          this.$store.dispatch('showSuccess', 'Installing app...');
        } else {
          this.$store.dispatch('showInfo', 'Installation cancelled');
        }
        
        // Clear the deferred prompt
        this.deferredPrompt = null;
        this.showInstallPrompt = false;
        
        // Save preference if checkbox was checked
        if (this.dontShowAgain) {
          localStorage.setItem('pwa-install-dont-show-again', 'true');
        }
      } catch (error) {
        console.error('Error during installation:', error);
        this.$store.dispatch('showError', 'Failed to install app');
      } finally {
        this.installing = false;
      }
    },
    
    dismissPrompt() {
      if (this.isMobile) {
        // On mobile, animate out
        this.isClosed = true;
        setTimeout(() => {
          this.showInstallPrompt = false;
          if (this.dontShowAgain) {
            localStorage.setItem('pwa-install-dont-show-again', 'true');
          } else {
            localStorage.setItem('pwa-install-dismissed', 'true');
          }
        }, 300);
      } else {
        // On desktop/tablet, dismiss immediately
        this.showInstallPrompt = false;
        if (this.dontShowAgain) {
          localStorage.setItem('pwa-install-dont-show-again', 'true');
        } else {
          localStorage.setItem('pwa-install-dismissed', 'true');
        }
      }
    },
    
    dismissIOSInstructions() {
      if (this.isMobile) {
        // On mobile, animate out
        this.isClosed = true;
        setTimeout(() => {
          this.showIOSInstructions = false;
          if (this.dontShowAgain) {
            localStorage.setItem('pwa-install-dont-show-again', 'true');
          } else {
            localStorage.setItem('pwa-ios-instructions-dismissed', 'true');
          }
        }, 300);
      } else {
        // On desktop/tablet, dismiss immediately
        this.showIOSInstructions = false;
        if (this.dontShowAgain) {
          localStorage.setItem('pwa-install-dont-show-again', 'true');
        } else {
          localStorage.setItem('pwa-ios-instructions-dismissed', 'true');
        }
      }
    },
    
    dismissManualInstall() {
      if (this.isMobile) {
        // On mobile, animate out
        this.isClosed = true;
        setTimeout(() => {
          this.showManualInstall = false;
          if (this.dontShowAgain) {
            localStorage.setItem('pwa-install-dont-show-again', 'true');
          } else {
            localStorage.setItem('pwa-manual-install-dismissed', 'true');
          }
        }, 300);
      } else {
        // On desktop/tablet, dismiss immediately
        this.showManualInstall = false;
        if (this.dontShowAgain) {
          localStorage.setItem('pwa-install-dont-show-again', 'true');
        } else {
          localStorage.setItem('pwa-manual-install-dismissed', 'true');
        }
      }
    }
  }
};
</script>

<style scoped>
.install-prompt-card {
  position: fixed;
  z-index: 1000;
  background: white !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06) !important;
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  display: none !important;
}

/* Desktop/Tablet Banner Style */
.install-prompt-card:not(.is-mobile) {
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  max-width: 100%;
  border-radius: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin: 0;
}

/* Mobile Bottom Popup Style */
.install-prompt-card.is-mobile {
  bottom: 0;
  padding-bottom: calc(12px + constant(safe-area-inset-bottom));
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  width: 100%;
  max-width: 100%;
  border-radius: 16px 16px 0 0;
  transform: translateY(0);
  opacity: 1;
  animation: slideUpFromBottom 0.3s ease-out;
}

.install-prompt-card.is-mobile.is-closed {
  transform: translateY(100%);
  opacity: 0;
}

@keyframes slideUpFromBottom {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.banner-layout {
  max-width: 100%;
  margin: 0;
  padding: 0 !important;
}

.banner-content-desktop {
  width: 100%;
  flex-wrap: nowrap !important;
  gap: 0;
  padding: 12px 24px;
  justify-content: flex-start;
  align-items: center;
}

.banner-icon {
  flex-shrink: 0;
}

.banner-text-container {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 400px;
}

.banner-actions {
  margin-left: auto;
}

.banner-content-desktop .install-title {
  margin-bottom: 1px;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.2;
  color: #1a202c;
}

.banner-content-desktop .install-subtitle {
  font-size: 0.8125rem;
  line-height: 1.2;
  color: #718096;
}

.dismiss-section {
  cursor: pointer;
  user-select: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.dismiss-section:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.dismiss-text {
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
}

.install-btn-black {
  border-color: #000 !important;
  color: #000 !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  width: auto !important;
  max-width: none !important;
  flex-shrink: 0;
}

.install-btn-black:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

.install-btn-black .v-icon {
  color: #000 !important;
}

.mobile-layout {
  padding: 16px !important;
}

.install-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 2px;
}

.install-subtitle {
  font-size: 0.875rem;
  color: #718096;
}

.manual-install,
.ios-instructions {
  background: white !important;
}

.ios-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #1a202c;
  font-size: 0.875rem;
  line-height: 1.5;
}

.step-icon {
  flex-shrink: 0;
}

.dont-show-checkbox {
  margin-top: 0;
  margin-bottom: 0;
}

.dont-show-checkbox ::v-deep .v-input__control {
  min-width: auto;
}

.dont-show-checkbox ::v-deep .v-input__slot {
  margin-bottom: 0;
}

.dont-show-checkbox ::v-deep .v-label {
  font-size: 0.875rem;
  color: #718096;
}

.action-buttons {
  display: flex;
  align-items: center;
}

/* Mobile-specific styles */
@media (max-width: 959px) {
  .install-title {
    font-size: 0.9rem;
  }
  
  .install-subtitle {
    font-size: 0.8rem;
  }
  
  .step-item {
    font-size: 0.8rem;
  }
  
  .mobile-actions {
    width: 100%;
  }
  
  .action-buttons {
    width: 100%;
  }
  
  .action-buttons .v-btn {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* Desktop/Tablet banner adjustments */
@media (min-width: 960px) {
  .banner-layout {
    padding: 0 !important;
  }
  
  .install-prompt-card:not(.is-mobile) .v-card-text {
    padding: 0 !important;
  }
  
  .banner-content-desktop {
    min-height: 52px;
    padding: 10px 24px;
  }
  
  .banner-content-desktop .action-buttons {
    flex-shrink: 0;
  }
  
  .banner-content-desktop .install-btn-black {
    padding: 6px 16px !important;
    min-width: auto !important;
    max-width: none !important;
    width: auto !important;
    height: 32px !important;
    font-size: 0.875rem !important;
    flex-shrink: 0;
  }
  
  .banner-content-desktop .banner-icon {
    margin-right: 12px;
  }
  
  .banner-content-desktop .banner-text-container {
    margin-right: 16px;
    flex: 0 1 auto;
  }
  
  .banner-content-desktop .dismiss-section {
    margin-right: 12px;
  }
}
</style>
